import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from './simple-modal';

/**
 * Example component demonstrating various theme variants
 * for the Simple Modal components following HeroUI patterns
 */
export const SimpleModalExamples = () => {
  const [openModals, setOpenModals] = useState<Record<string, boolean>>({});

  const openModal = (modalId: string) => {
    setOpenModals(prev => ({ ...prev, [modalId]: true }));
  };

  const closeModal = (modalId: string) => {
    setOpenModals(prev => ({ ...prev, [modalId]: false }));
  };

  const examples = [
    {
      id: 'default',
      title: 'Default Modal',
      variant: 'default' as const,
      description: 'Basic modal with default styling'
    },
    {
      id: 'primary',
      title: 'Primary Modal',
      variant: 'primary' as const,
      description: 'Primary themed modal for important actions'
    },
    {
      id: 'secondary',
      title: 'Secondary Modal',
      variant: 'secondary' as const,
      description: 'Secondary themed modal for alternative actions'
    },
    {
      id: 'success',
      title: 'Success Modal',
      variant: 'success' as const,
      description: 'Success themed modal for positive feedback'
    },
    {
      id: 'warning',
      title: 'Warning Modal',
      variant: 'warning' as const,
      description: 'Warning themed modal for caution messages'
    },
    {
      id: 'danger',
      title: 'Danger Modal',
      variant: 'danger' as const,
      description: 'Danger themed modal for critical actions'
    },
    {
      id: 'bordered',
      title: 'Bordered Modal',
      variant: 'bordered' as const,
      description: 'Modal with visible border styling'
    },
    {
      id: 'flat',
      title: 'Flat Modal',
      variant: 'flat' as const,
      description: 'Flat styled modal with minimal depth'
    },
    {
      id: 'shadow',
      title: 'Shadow Modal',
      variant: 'shadow' as const,
      description: 'Modal with enhanced shadow effects'
    },
  ];

  return (
    <div className="p-8 space-y-4">
      <h2 className="text-2xl font-bold mb-6">Simple Modal Theme Examples</h2>

      {/* Grid of example buttons */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {examples.map((example) => (
          <button
            key={example.id}
            onClick={() => openModal(example.id)}
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <h3 className="font-semibold">{example.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{example.description}</p>
          </button>
        ))}
      </div>

      {/* Size Examples */}
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-4">Size Examples</h3>
        <div className="flex gap-2 flex-wrap">
          {['xs', 'sm', 'md', 'lg', 'xl', '2xl', 'full'].map((size) => (
            <button
              key={size}
              onClick={() => openModal(`size-${size}`)}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200"
            >
              {size.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Backdrop Examples */}
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">Backdrop Examples</h3>
        <div className="flex gap-2 flex-wrap">
          {['opaque', 'blur', 'transparent'].map((backdrop) => (
            <button
              key={backdrop}
              onClick={() => openModal(`backdrop-${backdrop}`)}
              className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200"
            >
              {backdrop}
            </button>
          ))}
        </div>
      </div>

      {/* Render all example modals */}
      {examples.map((example) => (
        <Modal
          key={example.id}
          isOpen={openModals[example.id] || false}
          onClose={() => closeModal(example.id)}
          variant={example.variant}
          size="md"
        >
          <ModalContent>
            <ModalHeader variant={example.variant}>
              {example.title}
            </ModalHeader>
            <ModalBody variant={example.variant}>
              <p>{example.description}</p>
              <p className="mt-2 text-sm opacity-80">
                                This modal uses the <code>{example.variant}</code> variant theme.
              </p>
            </ModalBody>
            <ModalFooter variant={example.variant}>
              <button
                onClick={() => closeModal(example.id)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
              >
                                Close
              </button>
              <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
                                Action
              </button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ))}

      {/* Size example modals */}
      {['xs', 'sm', 'md', 'lg', 'xl', '2xl', 'full'].map((size) => (
        <Modal
          key={`size-${size}`}
          isOpen={openModals[`size-${size}`] || false}
          onClose={() => closeModal(`size-${size}`)}
          variant="default"
          size={size as any}
        >
          <ModalContent>
            <ModalHeader>
              {size.toUpperCase()} Size Modal
            </ModalHeader>
            <ModalBody>
              <p>This modal demonstrates the <strong>{size}</strong> size variant.</p>
              <p className="mt-2 text-sm opacity-80">
                                Each size has predefined width constraints that adapt responsively.
              </p>
            </ModalBody>
            <ModalFooter>
              <button
                onClick={() => closeModal(`size-${size}`)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
              >
                                Close
              </button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ))}

      {/* Backdrop example modals */}
      {['opaque', 'blur', 'transparent'].map((backdrop) => (
        <Modal
          key={`backdrop-${backdrop}`}
          isOpen={openModals[`backdrop-${backdrop}`] || false}
          onClose={() => closeModal(`backdrop-${backdrop}`)}
          variant="default"
          backdrop={backdrop as any}
          size="md"
        >
          <ModalContent>
            <ModalHeader>
              {backdrop} Backdrop
            </ModalHeader>
            <ModalBody>
              <p>This modal demonstrates the <strong>{backdrop}</strong> backdrop style.</p>
              <p className="mt-2 text-sm opacity-80">
                                Different backdrop styles provide varying levels of focus and visual separation.
              </p>
            </ModalBody>
            <ModalFooter>
              <button
                onClick={() => closeModal(`backdrop-${backdrop}`)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
              >
                                Close
              </button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ))}
    </div>
  );
};

export default SimpleModalExamples;
